import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  FileText, Download, Calendar, Tag, ExternalLink, Search
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { TrustNavigation } from "@/components/TrustNavigation";
import { TrustFooter } from "@/components/TrustFooter";
import documentsData from "@/data/documents.json";

export default function DocumentLibrary() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = Array.from(new Set(documentsData.map(doc => doc.category)));
  
  const filteredDocuments = documentsData.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.summary.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <TrustNavigation currentPath="/document-library" />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 py-16 md:py-24 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium border border-white/20 mb-6">
              <FileText className="w-4 h-4" />
              <span>Document Library</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Policy & Process Documents
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Access versioned documentation for service agreements, compliance policies, and process specifications.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-400"
                  data-testid="input-document-search"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedCategory === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(null)}
                  data-testid="button-filter-all"
                >
                  All
                </Button>
                {categories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                    data-testid={`button-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-4" data-testid="document-list">
              {filteredDocuments.map((doc) => (
                <Card key={doc.id} className="p-6 bg-slate-800/30 border-white/10 hover:bg-slate-800/50 transition-shadow" data-testid={`document-card-${doc.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-slate-400" />
                        <h3 className="font-semibold text-white" data-testid={`text-document-title-${doc.id}`}>{doc.title}</h3>
                      </div>
                      <p className="text-sm text-slate-400 mb-3">{doc.summary}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                        <div className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          <span>{doc.category}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">v{doc.version}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>Updated {doc.lastUpdated}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {doc.contactRequired ? (
                        <Link href="/contact">
                          <Button size="sm" className="gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid={`button-request-${doc.id}`}>
                            <ExternalLink className="w-4 h-4" />
                            Request Access
                          </Button>
                        </Link>
                      ) : (
                        <Link href={doc.downloadUrl}>
                          <Button size="sm" className="gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid={`button-view-${doc.id}`}>
                            <ExternalLink className="w-4 h-4" />
                            View
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {filteredDocuments.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No documents found matching your criteria.</p>
              </div>
            )}

            <Card className="p-6 bg-slate-800/20 border-white/10 mt-8">
              <p className="text-sm text-slate-400">
                <strong>Note:</strong> PDF documents are provided for reference. For the most current versions 
                of policies and procedures, please contact your account representative or submit an enquiry. 
                References available upon request.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <TrustFooter />
    </div>
  );
}
